<!-- filepath: /src/components/UnitMarkers.vue -->
<template>
    <template v-for="unit in units" :key="unit.unit_id">
        <l-marker :title="unit.name" :lat-lng="getMarkerPosition(unit)">
            <l-icon :icon-size="[32, 32]" :icon-anchor="[16, 16]" class-name="point-event">
                <div class="pointicon" :class="{
                    outside: isUnitOutsideBounds(
                        [unit.location.coordinates[1], unit.location.coordinates[0]],
                        screenBounds
                    )
                }">
                    <div class="d-flex justify-content-center align-items-center" :style="{
                        width: isUnitOutsideBounds(
                            [unit.location.coordinates[1], unit.location.coordinates[0]],
                            screenBounds
                        )
                            ? '24px'
                            : '40px',
                        height: isUnitOutsideBounds(
                            [unit.location.coordinates[1], unit.location.coordinates[0]],
                            screenBounds
                        )
                            ? '24px'
                            : '40px',
                        borderRadius: '50%'
                    }">
                        <template v-if="unit.rol === 15">
                                                    <i
                                                        class="bi bi-shield-check unit-icon unit-icon-svg"
                                                        aria-label="Unit Icon"
                                                        :style="{ color: intToHexColor(unit.color) }"
                                                    ></i>
                        </template>
                        <template v-else>
                          <img :src="'/images/unit_' + unit.unit_id + '.png'" alt="Unit Icon" :style="{
                              width: isUnitOutsideBounds(
                                  [unit.location.coordinates[1], unit.location.coordinates[0]],
                                  screenBounds
                              )
                                  ? '24px'
                                  : '40px',
                              height: isUnitOutsideBounds(
                                  [unit.location.coordinates[1], unit.location.coordinates[0]],
                                  screenBounds
                              )
                                  ? '24px'
                                  : '40px',
                              transform: unit.rol === 1 ? `rotate(${unit.cog}deg)` : undefined
                          }" />
                        </template>
                    </div>
                </div>
                <div class="unit-tag" v-if="
                    !isUnitOutsideBounds(
                        [unit.location.coordinates[1], unit.location.coordinates[0]],
                        screenBounds
                    ) && unit.estado === 1
                ">
                    {{ unit.name }}<br />
                    {{ useUtils().metersToFeet(unit.alt, 0) }} ft
                </div>
            </l-icon>

            <l-tooltip  v-if="unit.rol !== 15">
                Misión: {{ unit.mission }}<br />
                Piloto: {{ unit.pilot }} <br />
                Copiloto: {{ unit.copilot }}<br />
                Cámara: {{ unit.camera }}
            </l-tooltip>
            <l-tooltip  v-else >
                Misión: {{ unit.mission }}<br />
                Patrulla: {{ unit.pilot }} <br />
            </l-tooltip>
            
        </l-marker>
        <!-- Polígono de la cámara -->
        <l-polygon v-if="
            unit.estado === 1 &&
            unit.cam_polygon &&
            Array.isArray(unit.cam_polygon.coordinates) &&
            isPolygonValid(unit.cam_polygon.coordinates, unit)
        " :lat-lngs="unit.cam_polygon.coordinates.map((coord: [number, number]) => {
            const [lng, lat] = coord
            return [lat, lng]
        })
            " :color="'#2196f3'" :weight="2" :opacity="0.6" :fill-opacity="0.15" />
        <!-- Línea entre sensor y centro del video -->
        <l-polyline v-if="unit.estado === 1 && isPolygonValid(unit.cam_polygon.coordinates, unit)" :lat-lngs="[
            [Number(unit.location.coordinates[1]), Number(unit.location.coordinates[0])],
            [Number(unit.cam_location.coordinates[1]), Number(unit.cam_location.coordinates[0])]
        ]" :color="'#808080'" :weight="3" :opacity="0.5" />

        <!-- Track del avión -->
        <l-polyline v-if="global.getUnitTrackVisibility(unit.unit_id) && getUnitCurvePoints(unit.unit_id).length"
            :lat-lngs="getUnitCurvePoints(unit.unit_id)" :color="'#ff6600'" :weight="3" :opacity="0.5" />
    </template>
</template>

<script setup lang="ts">
import { useUnits } from '@/composables/useUnits'
import { useUtils } from '@/composables/useUtils';
const { intToHexColor } = useUtils();
import { LMarker, LIcon, LTooltip, LPolygon, LPolyline } from '@vue-leaflet/vue-leaflet'
import { toRef } from 'vue'
import { useGlobalStore } from '@/stores/global'


const global = useGlobalStore()


const props = defineProps<{
    units: any[]
    map: any
    screenBounds: any
    getLayerStyle: Function
}>()

const { getMarkerPosition, isUnitOutsideBounds, unitColor } = useUnits(
    toRef(props, 'map'),
    toRef(props, 'screenBounds')
)
// Calcula la distancia entre dos puntos [lat, lng] en km usando la fórmula de Haversine
function haversineDistance(a: [number, number], b: [number, number]): number {
    const toRad = (x: number) => (x * Math.PI) / 180
    const R = 6371 // Radio de la Tierra en km
    const dLat = toRad(b[0] - a[0])
    const dLng = toRad(b[1] - a[1])
    const lat1 = toRad(a[0])
    const lat2 = toRad(b[0])
    const aVal = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
    const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal))
    return R * c
}
// Función reactiva para obtener los puntos de track de una unidad específica
function getUnitCurvePoints(unitId: string): [number, number][] {
    const trackPoints = global.getUnitTrack(unitId)
    return trackPoints.map(point => [
        Number(point.location.coordinates[1]), // lat
        Number(point.location.coordinates[0])  // lng
    ])
}
// Verifica que ninguna cara del polígono sea mayor a 10km Y que el centro del polígono esté a menos de 50km del avión
function isPolygonValid(coords: [number, number][], unit: any): boolean {
    if (!Array.isArray(coords) || coords.length < 2) return false

    // Verificar que ninguna cara sea mayor a 10km
    for (let i = 0; i < coords.length - 1; i++) {
        const [lng1, lat1] = coords[i]
        const [lng2, lat2] = coords[i + 1]
        const dist = haversineDistance([lat1, lng1], [lat2, lng2])
        if (dist > 10) return false
    }
    // Cierra el polígono (último con primero)
    const [lng1, lat1] = coords[coords.length - 1]
    const [lng2, lat2] = coords[0]
    if (haversineDistance([lat1, lng1], [lat2, lng2]) > 10) return false

    // Verificar que el centro del polígono (cam_location) esté a menos de 50km del avión (location)
    if (unit.cam_location && unit.location) {
        const distanceToUnit = haversineDistance(
            [unit.location.coordinates[1], unit.location.coordinates[0]], // [lat, lng] del avión
            [unit.cam_location.coordinates[1], unit.cam_location.coordinates[0]] // [lat, lng] del centro del polígono
        )
        if (distanceToUnit > 50) return false
    }

    return true
}
</script>
<style scoped>
.pointicon.outside {
    /* Elimina el borde rojo, solo cambia el tamaño por JS */
    border: none;
}

.unit-tag {
    position: relative;
    bottom: 5px;
    right: -120%;
    z-index: 10;
    background: rgba(255, 255, 255, 0.5);
    color: #000000;
    border-radius: 4px;
    padding: 2px 2px;
    font-size: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    pointer-events: none;
    white-space: nowrap;
    min-width: max-content;
    text-align: center;
}
.unit-icon-svg {
  font-size: 24px;
  width: 24px;
  height: 24px;
  display: inline-block;
  vertical-align: middle;
  color: rgb(62, 131, 227);
}
.unit-icon {
  width: 24px;
  height: 24px;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  pointer-events: none;
}
</style>
